import { ReactNode } from 'react';
import ChevronBackIcon from '../assets/icons/chevron-back.svg?react';
import HamburgerIcon from '../assets/icons/hamburger.svg?react';
import { useHeader } from '../contexts/HeaderContext';
import { useMobileSidebar } from '../contexts/MobileSidebarContext';
import { useLayoutTier } from '../hooks/useLayoutTier';
import { cn } from '../utils/className';
import { AccountButton } from './AccountButton';
import { AppBarIconButton } from './AppBarIconButton';

// Register the app switcher web component
import '@tomra/tf-app-switcher';
import { PRODUCT_LOGO } from '../demo/demoMode';
import { APP_BAR_TONES } from './appBarTones';

interface HeaderProps {
  mobileActionButton?: ReactNode;
}

export function Header({ mobileActionButton }: HeaderProps) {
  const { toggle } = useMobileSidebar();
  const {
    mobileActionButton: contextButton,
    pageTitle,
    backAction,
    appBarTone,
  } = useHeader();
  const { isTabletDown } = useLayoutTier();

  // Use prop if provided, otherwise use context
  const actionButton = mobileActionButton ?? contextButton;

  return (
    <header
      className={cn(
        `border-border-primary flex shrink-0 items-center justify-between
        border-b py-4`,
        isTabletDown &&
          'min-h-(--spacing-app-bar-height) border-b-0 bg-linear-68 px-4 py-3',
        isTabletDown && APP_BAR_TONES[appBarTone],
        !isTabletDown && 'h-header-height bg-surface-primary px-6'
      )}
    >
      {isTabletDown ? (
        <>
          <AppBarIconButton
            aria-label={backAction ? 'Back' : 'Toggle menu'}
            icon={
              backAction ? (
                <ChevronBackIcon className='text-blue-050' />
              ) : (
                <HamburgerIcon className='fill-white [&_path]:fill-white' />
              )
            }
            onClick={backAction ?? toggle}
            className='bg-transparent'
          />
          {pageTitle ? (
            <h1
              className='size-h4 text-inverted min-w-0 flex-1 truncate pr-2 pl-3
                font-bold'
            >
              {pageTitle}
            </h1>
          ) : (
            <img
              src='/logo-mobile-header.svg'
              alt='Tomra Logo'
              className='h-4'
            />
          )}
        </>
      ) : (
        <div className='flex items-center gap-2'>
          <tf-app-switcher current-app='pulse' position='left' theme='light' />
          <div className='flex items-end gap-2'>
            <img
              src='/tomra-logo-text.svg'
              alt='Tomra Logo'
              className='h-3.5'
            />
            <img
              src={PRODUCT_LOGO.src}
              alt={PRODUCT_LOGO.alt}
              className='h-2.5'
            />
          </div>
        </div>
      )}
      <div className='flex items-center gap-2'>
        {isTabletDown ? (
          actionButton && (
            <div className='text-surface-primary'>{actionButton}</div>
          )
        ) : (
          <AccountButton className='flex'></AccountButton>
        )}
      </div>
    </header>
  );
}
